import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import {
  LIST_PAGE_LIMIT,
  LIST_PAGE_LIMIT_OPTIONS,
  STATUS_CHIPS,
  asNum,
  copyText,
  coinOptionLabel,
  dateToFromIso,
  dateToToIso,
  formatDateShort,
  formatMoneyList,
  isUnauthorized,
  packageOptionLabel,
  userDisplayName,
} from "./stakingStatementUtils";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "./StakingStakesPage.css";
import "./StakingStatementPage.css";

const EMPTY_PAGINATION = {
  page: 1,
  limit: LIST_PAGE_LIMIT,
  total: 0,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};

/** Backend caps staking-statement list at 50 per request. */
const EXPORT_PAGE_LIMIT = 50;
const EXPORT_MAX_PAGES = 2000;

const exportAmount = (value) => {
  if (value == null || value === "") return "";
  const n = asNum(value);
  return Number.isFinite(n) ? n : "";
};

const statementListRowToExport = (row, currencyRow) => {
  const user = row?.user || {};
  const counts = row?.plan_counts || {};
  const name = userDisplayName(user);
  return {
    Name: name === "—" ? "" : name,
    UUID: user.uuid ?? "",
    Email: user.email ?? "",
    Phone: user.phone ?? "",
    "Payout Status": user.payout_status ?? "",
    "Active Plans": counts.active ?? "",
    "Completed Plans": counts.completed ?? "",
    "Cancelled Plans": counts.cancelled ?? "",
    "Total Plans": counts.total ?? "",
    Currency: currencyRow?.currency ?? "",
    Staked: currencyRow ? exportAmount(currencyRow.staked) : "",
    "Payout Paid": currencyRow ? exportAmount(currencyRow.payout_paid) : "",
    "Payout Remaining": currencyRow ? exportAmount(currencyRow.payout_remaining) : "",
    "Interest Paid": currencyRow ? exportAmount(currencyRow.interest_paid) : "",
    "Interest Remaining": currencyRow ? exportAmount(currencyRow.interest_remaining) : "",
    "Last Staked": formatDateShort(row?.last_staked_at),
  };
};

const StakingStatementPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(LIST_PAGE_LIMIT);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [planId, setPlanId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [coins, setCoins] = useState([]);
  const [packages, setPackages] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 700);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, statusFilter, currencyId, planId, from, to, pageLimit]);

  useEffect(() => {
    AdminService.getCoinsList()
      .then((res) => setCoins(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setCoins([]));
    AdminService.getEarningPackages({ page: 1, limit: 100, status: "all" })
      .then((res) => setPackages(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setPackages([]));
  }, []);

  const hasFilters =
    Boolean(debouncedUser) ||
    Boolean(statusFilter) ||
    Boolean(currencyId) ||
    Boolean(planId) ||
    Boolean(from) ||
    Boolean(to);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedUser) params.user = debouncedUser;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (currencyId) params.currency_id = currencyId;
      if (planId) params.plan_id = planId;
      const fromIso = dateToFromIso(from);
      const toIso = dateToToIso(to);
      if (fromIso) params.from = fromIso;
      if (toIso) params.to = toIso;
      return params;
    },
    [page, pageLimit, debouncedUser, statusFilter, currencyId, planId, from, to]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getStakingStatement(buildParams());
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      const pag = res?.pagination || {};
      setPagination({
        page: pag.page || page,
        limit: pag.limit || pageLimit,
        total: typeof pag.total === "number" ? pag.total : list.length,
        total_pages: Math.max(1, Number(pag.total_pages) || 1),
        has_next: typeof pag.has_next === "boolean" ? pag.has_next : false,
        has_prev: typeof pag.has_prev === "boolean" ? pag.has_prev : false,
      });
      if (res?.success === false) {
        setError(res?.message || "Failed to fetch staking statement");
      }
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to fetch staking statement");
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, pageLimit]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleReset = () => {
    setUserSearch("");
    setDebouncedUser("");
    setStatusFilter("");
    setCurrencyId("");
    setPlanId("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const handleCopyUuid = async (e, uuid) => {
    e.stopPropagation();
    const ok = await copyText(uuid);
    if (ok) toast.success("UUID copied");
    else toast.error("Could not copy UUID");
  };

  const openDetail = (userId) => {
    if (!userId) return;
    navigate(`/dashboard/staking/statement/${userId}`);
  };

  const fetchAllFilteredUsers = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getStakingStatement(
        buildParams(currentPage, EXPORT_PAGE_LIMIT)
      );
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return null;
      }
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = Math.max(1, Number(res?.pagination?.total_pages) || 1);
      const hasNext =
        typeof res?.pagination?.has_next === "boolean"
          ? res.pagination.has_next
          : currentPage < lastPage;
      currentPage += 1;
      if (!hasNext) break;
    } while (currentPage <= lastPage && currentPage <= EXPORT_MAX_PAGES);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredUsers();
      if (!allRows) return;
      if (!allRows.length) {
        toast.error("No staking users match your filters to export.");
        return;
      }

      const exportData = allRows.flatMap((row) => {
        const currencies = Array.isArray(row.by_currency) ? row.by_currency : [];
        const lines = currencies.length ? currencies : [null];
        return lines.map((currencyRow) => statementListRowToExport(row, currencyRow));
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Staking Statement");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `staking_statement_${dateStamp}.xlsx`);
      toast.success(`Exported ${allRows.length} user(s) to Excel.`);
    } catch (err) {
      toast.error(err?.message || "Failed to export Excel file.");
    } finally {
      setExportLoading(false);
    }
  };

  const pageStart = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = rows.length === 0 ? 0 : pageStart + rows.length - 1;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking Statement</span>
          </p>
          <h1 className="dashboard-hero__title">Staking Statement</h1>
          <p className="dashboard-hero__subtitle">
            Paid vs remaining payout and interest, per user
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-invoice" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-users" />
              </span>
              <h2 className="openorders-section__title">Users</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{rows.length}</strong> on page
                {pagination.total != null && (
                  <>
                    {" "}
                    · <strong>{pagination.total}</strong> total
                  </>
                )}
              </span>
              {pagination.total_pages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{pagination.page}</strong> / <strong>{pagination.total_pages}</strong>
                </span>
              )}
              <button
                type="button"
                className="staking-stakes__export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered staking statement as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          <div className="ss-filters">
            <input
              type="search"
              placeholder="Email, UUID, phone"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input ss-filters__search"
            />
            <div className="ss-chips" role="tablist" aria-label="Plan status">
              {STATUS_CHIPS.map((chip) => (
                <button
                  key={chip.value || "all"}
                  type="button"
                  className={`ss-chip ${statusFilter === chip.value ? "ss-chip--active" : ""}`}
                  onClick={() => setStatusFilter(chip.value)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <select
              value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              className="openorders-search-input"
            >
              <option value="">All currencies</option>
              {coins
                .filter((c) => c._id || c.id)
                .map((c) => {
                  const id = c._id || c.id;
                  return (
                    <option key={id} value={id}>
                      {coinOptionLabel(c)}
                    </option>
                  );
                })}
            </select>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="openorders-search-input"
            >
              <option value="">All packages</option>
              {packages.map((pkg) => {
                const id = pkg._id || pkg.id;
                return (
                  <option key={id} value={id}>
                    {packageOptionLabel(pkg)}
                  </option>
                );
              })}
            </select>
            <label className="staking-page-limit">
              <span>From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <label className="staking-page-limit">
              <span>To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <button type="button" className="cp-btn cp-btn--outline" onClick={handleReset}>
              Reset
            </button>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="ss-error-banner">
              <p>{error}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={fetchRows}>
                Retry
              </button>
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table ss-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>UUID</th>
                      <th>Email</th>
                      <th>Plans</th>
                      <th>Currency</th>
                      <th>Staked</th>
                      <th>Payout paid</th>
                      <th>Payout remaining</th>
                      <th>Interest paid</th>
                      <th>Interest remaining</th>
                      <th>Last staked</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.flatMap((row) => {
                      const user = row.user || {};
                      const userId = user.id || user._id;
                      const currencies = Array.isArray(row.by_currency) ? row.by_currency : [];
                      const lines = currencies.length ? currencies : [null];
                      const span = lines.length;
                      const paused = String(user.payout_status || "").toLowerCase() === "inactive";
                      const counts = row.plan_counts || {};
                      const name = userDisplayName(user);

                      return lines.map((ccy, index) => (
                        <tr
                          key={`${userId || user.uuid || name}-${ccy?.currency_id || ccy?.currency || index}`}
                          className={`ss-row ${index > 0 ? "ss-row--nested" : ""}`}
                          onClick={() => openDetail(userId)}
                        >
                          {index === 0 ? (
                            <>
                              <td rowSpan={span}>
                                <div className="ss-user-cell">
                                  <strong>{name}</strong>
                                  {paused ? (
                                    <span className="ss-paused-badge">Payout paused</span>
                                  ) : null}
                                </div>
                              </td>
                              <td rowSpan={span} onClick={(e) => e.stopPropagation()}>
                                {user.uuid ? (
                                  <span className="ss-copy-cell">
                                    <code>{user.uuid}</code>
                                    <button
                                      type="button"
                                      className="ss-copy-btn"
                                      title="Copy UUID"
                                      aria-label="Copy UUID"
                                      onClick={(e) => handleCopyUuid(e, user.uuid)}
                                    >
                                      <i className="fas fa-copy" />
                                    </button>
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td rowSpan={span}>{user.email || "—"}</td>
                              <td rowSpan={span}>
                                {counts.total != null
                                  ? `${counts.active ?? 0} / ${counts.total} Active`
                                  : "—"}
                              </td>
                            </>
                          ) : null}
                          <td className="ss-ccy">{ccy?.currency || "—"}</td>
                          <td>{ccy ? formatMoneyList(ccy.staked, ccy.currency) : "—"}</td>
                          <td className="ss-amt ss-amt--paid">
                            {ccy ? formatMoneyList(ccy.payout_paid, ccy.currency) : "—"}
                          </td>
                          <td className="ss-amt ss-amt--remain">
                            {ccy ? formatMoneyList(ccy.payout_remaining, ccy.currency) : "—"}
                          </td>
                          <td>{ccy ? formatMoneyList(ccy.interest_paid, ccy.currency) : "—"}</td>
                          <td>{ccy ? formatMoneyList(ccy.interest_remaining, ccy.currency) : "—"}</td>
                          {index === 0 ? (
                            <>
                              <td rowSpan={span}>{formatDateShort(row.last_staked_at)}</td>
                              <td rowSpan={span} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="staking-stakes__view-btn"
                                  onClick={() => openDetail(userId)}
                                >
                                  <i className="fas fa-eye" /> View statement
                                </button>
                              </td>
                            </>
                          ) : null}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ss-pagination">
                <label className="staking-page-limit">
                  <span>Rows</span>
                  <select
                    value={pageLimit}
                    onChange={(e) => setPageLimit(Number(e.target.value))}
                    className="openorders-search-input staking-page-limit__select"
                  >
                    {LIST_PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_prev || loading || page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="openorders-stat" style={{ margin: 0 }}>
                  Showing {pageStart}–{pageEnd}
                  {pagination.total != null ? ` of ${pagination.total}` : ""}
                  {pagination.total_pages > 1
                    ? ` · Page ${pagination.page} of ${pagination.total_pages}`
                    : ""}
                </span>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_next || loading || page >= pagination.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <div className="ss-empty">
              <i className="fas fa-inbox" />
              <p>No staking users found</p>
              {hasFilters ? (
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleReset}>
                  Clear filters
                </button>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default StakingStatementPage;
